import { Children, isValidElement } from 'react';
import type { Components } from 'react-markdown';

import InlineChatLineChart, {
  type InlineChatChartSpec,
} from '../chart/InlineChatLineChart';

/** Shared react-markdown overrides for chat / task bubbles (scrollable tables, readable thead). */
export const chatMarkdownComponents: Components = {
  pre: ({ children, ...props }) => {
    const child = Children.toArray(children)[0];
    if (isValidElement(child) && child.type === InlineChatLineChart) {
      return <>{child}</>;
    }
    return <pre {...props}>{children}</pre>;
  },
  code: ({ children, className, ...props }) => {
    const { inline, ...codeProps } = props as typeof props & { inline?: boolean };
    const isChartBlock = !inline && className?.includes('language-chart');

    if (isChartBlock) {
      try {
        const spec = JSON.parse(String(children).trim()) as InlineChatChartSpec;
        return <InlineChatLineChart spec={spec} />;
      } catch {
        // Fall through to default code rendering below.
      }
    }

    return (
      <code className={className} {...codeProps}>
        {children}
      </code>
    );
  },
  table: ({ children, ...props }) => (
    <div className="my-3 max-w-full overflow-x-auto rounded-lg border border-app-border/70 bg-app-muted/25 shadow-sm">
      <table className="min-w-full border-collapse text-left" {...props}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }) => (
    <thead className="bg-app-muted/60 border-b border-app-border" {...props}>
      {children}
    </thead>
  ),
  th: ({ children, ...props }) => (
    <th
      className="border border-app-border/80 px-3 py-2 font-semibold text-app-text whitespace-nowrap"
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td className="border border-app-border/60 px-3 py-2 align-top text-app-text-sec" {...props}>
      {children}
    </td>
  ),
  tr: ({ children, ...props }) => (
    <tr className="even:bg-app-muted/20" {...props}>
      {children}
    </tr>
  ),
};
